// src/pages/player/TeamRequests.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import ProfileImage from "../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";
const TeamRequests = () => {
  const { t } = useTranslation();
  const [uniqueName, setUniqueName] = useState("");
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { updatePageTitle } = useMainPageTitle();
  const [teamRequests, setTeamRequests] = useState(null);
  const [fetchingRequests, setFetchingRequests] = useState(true);

  useEffect(() => {
    updatePageTitle(t("player.teamRequests"), t("player.viewTeamRequests"));
  }, [updatePageTitle, t]);

  const handleSearch = async () => {
    if (!uniqueName.trim()) {
      setError(t("player.enterTeamName"));
      return;
    }

    setLoading(true);
    setTeam(null);
    setError("");

    try {
      const response = await fetch(
        `${API_URL}/team/search-team-by-team-unique-name?uniqueName=${encodeURIComponent(
          uniqueName.trim()
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await response.json();

      if (data.team) {
        setTeam(data.team);
      } else {
        setError(data.message || t("player.teamNotFound"));
      }
    } catch (err) {
      setError(t("player.errorSearchingTeam"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async () => {
    if (!team) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("Sending team request with data:", { teamId: team._id });
      console.log("Authorization token:", localStorage.getItem("token") ? "Token exists" : "No token");
      
      const response = await fetch(
        `${API_URL}/team/send-request-to-join-team`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ teamId: team._id }),
        }
      );

      const data = await response.json();
      console.log("Send request response:", data);
      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (response.ok && data.success) {
        setSuccess(t("player.requestSent"));
        setTeam(null);
        setUniqueName("");
        setError(""); // Clear any previous errors
        
        // Fetch updated requests and ensure we transition to the request view
        await fetchTeamRequests();
        
        // Clear success message after a delay
        setTimeout(() => {
          setSuccess("");
        }, 3000);
      } else {
        setError(data.error || data.message || t("player.failedToSendJoinRequest"));
      }
    } catch (err) {
      console.error("Error sending join request:", err);
      setError(t("player.errorSendingJoinRequest"));
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRequests = async () => {
    setFetchingRequests(true);
    try {
      const response = await fetch(`${API_URL}/team/get-my-requests-status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      console.log('Data from server:', data);
      
      // Transform the data to match the expected format
      if (response.ok && data.success && data.data && data.data.requests && data.data.requests.length > 0) {
        const request = data.data.requests[0]; // Get the first (and likely only) request
        const formattedRequest = {
          team: request.team,
          teamRequest: {
            _id: request._id,
            status: request.status,
            message: request.message,
            createdAt: request.createdAt,
            updatedAt: request.updatedAt
          }
        };
        console.log('Setting teamRequests to:', formattedRequest);
        setTeamRequests(formattedRequest);
      } else {
        // No requests found
        console.log('No team requests found, setting to null');
        setTeamRequests(null);
      }
    } catch (error) {
      console.error('Error fetching team requests:', error);
      setTeamRequests(null);
    } finally {
      setFetchingRequests(false);
    }
  };

  useEffect(() => {
    fetchTeamRequests();
  }, []);

  // Add this useEffect to see when teamRequests actually updates
  useEffect(() => {
    console.log("Updated teamRequests:", teamRequests);
  }, [teamRequests]);

  const handleDeleteRequest = async () => {
    try {
      const response = await fetch(`${API_URL}/team/delete-my-request-to-join-team`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      console.log("Delete request response:", data);
      if (response.ok && data.success) {
        // Fetch updated requests instead of full page reload
        await fetchTeamRequests();
      } else {
        setError(data.error || data.message || t("player.failedToDeleteRequest"));
      }
    } catch (error) {
      console.error("Error deleting team request:", error);
      setError(t("player.errorDeletingRequest"));
    }
  };
  
  return (
    <div className=" py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("player.teamRequests")}</h1>
          <p className="text-lg text-gray-600">{t("player.manageTeamRequests")}</p>
        </div>

        {/* Loading State */}
        {fetchingRequests && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="px-6 py-4" style={{background: 'linear-gradient(to right, #E26735, #D45420)'}}>
                <h2 className="text-xl font-bold text-white text-center">
                  {t("player.loadingRequests")}
                </h2>
              </div>
              <div className="p-8 text-center">
                <svg className="animate-spin h-8 w-8 text-orange-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 mt-4">{t("player.fetchingRequests")}</p>
              </div>
            </div>
          </div>
        )}

        {!fetchingRequests && teamRequests?.team && (
          <div className="max-w-2xl mx-auto mb-8">
            {/* Status Badge */}
            <div className="flex justify-center mb-6">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                teamRequests.teamRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                teamRequests.teamRequest.status === 'approved' ? 'bg-green-100 text-green-800' :
                teamRequests.teamRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  teamRequests.teamRequest.status === 'pending' ? 'bg-yellow-500 animate-pulse' :
                  teamRequests.teamRequest.status === 'approved' ? 'bg-green-500' :
                  teamRequests.teamRequest.status === 'rejected' ? 'bg-red-500' :
                  'bg-gray-500'
                }`}></div>
                {t("player.requestStatus", { status: t(`common.${teamRequests.teamRequest.status}`) })}
              </div>
            </div>

            {/* Main Request Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300">
              {/* Card Header with gradient */}
              <div className="px-6 py-4" style={{background: 'linear-gradient(to right, #E26735, #D45420)'}}>
                <h2 className="text-xl font-bold text-white text-center">
                  {t("player.yourTeamRequest")}
            </h2>
              </div>

              <div className="p-8">
                {/* Team Info Section */}
                <div className="text-center mb-8">
                  <div className="relative inline-block mb-4">
              <ProfileImage
                      className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-lg hover:ring-orange-200 transition-all duration-300"
                alt={teamRequests.team.name}
                image={teamRequests.team.logo}
              />
                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white ${
                      teamRequests.teamRequest.status === 'pending' ? 'bg-yellow-500' :
                      teamRequests.teamRequest.status === 'approved' ? 'bg-green-500' :
                      teamRequests.teamRequest.status === 'rejected' ? 'bg-red-500' :
                      'bg-gray-500'
                    }`}></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{teamRequests.team.name}</h3>
                  <p className="text-lg text-gray-500 mb-4">@{teamRequests.team.uniqueName}</p>
                  
              {teamRequests.team.sport && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 mb-4">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                      </svg>
                  {teamRequests.team.sport}
                    </div>
              )}
                </div>

                {/* Request Details */}
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center md:text-left">
                      <p className="text-sm font-medium text-gray-500 mb-1">{t("player.status")}</p>
                      <p className={`text-lg font-semibold capitalize ${
                        teamRequests.teamRequest.status === 'pending' ? 'text-yellow-600' :
                        teamRequests.teamRequest.status === 'approved' ? 'text-green-600' :
                        teamRequests.teamRequest.status === 'rejected' ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {t(`common.${teamRequests.teamRequest.status}`)}
                      </p>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="text-sm font-medium text-gray-500 mb-1">{t("player.submitted")}</p>
                      <p className="text-lg font-semibold text-gray-700">
                        {new Date(teamRequests.teamRequest.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {teamRequests.teamRequest.message && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-500 mb-2">{t("player.yourMessage")}</p>
                      <p className="text-gray-700 italic bg-white p-3 rounded-lg border">
                        "{teamRequests.teamRequest.message}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                {teamRequests.teamRequest.status === 'pending' && (
                  <div className="text-center">
              <button
                onClick={handleDeleteRequest}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {t("player.cancelRequest")}
              </button>
            </div>
                )}
              </div>
          </div>
        </div>
      )}

        {!fetchingRequests && !teamRequests?.team && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              {/* Header with gradient */}
              <div className="px-6 py-6" style={{background: 'linear-gradient(to right, #E26735, #C4501F)'}}>
                <h2 className="text-2xl font-bold text-white text-center">
              {t("player.joinTeam")}
            </h2>
                <p className="text-orange-100 text-center mt-2">
                  {t("player.searchTeamsDescription")}
                </p>
              </div>

              <div className="p-8">
            {/* Search Form */}
                <div className="mb-8">
              <label
                htmlFor="uniqueName"
                    className="block text-sm font-semibold text-gray-700 mb-3"
              >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                {t("player.teamUniqueName")}
              </label>
                  <div className="flex gap-3">
                <input
                  type="text"
                  id="uniqueName"
                  value={uniqueName}
                  onChange={(e) => setUniqueName(e.target.value)}
                      className="flex-1 shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 block w-full sm:text-sm border-gray-300 rounded-lg px-4 py-3 bg-gray-50 transition-all duration-200"
                      placeholder={t("player.teamUniqueNamePlaceholder")}
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
                      style={{
                        background: loading ? '#D1D5DB' : 'linear-gradient(to right, #E26735, #D45420)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #D45420, #C4501F)'
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.target.style.background = 'linear-gradient(to right, #D45420, #C4501F)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.target.style.background = 'linear-gradient(to right, #E26735, #D45420)';
                        }
                      }}
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t("player.searching")}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          {t("player.searchTeam")}
                        </>
                      )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
                  <div className="rounded-lg bg-red-50 p-4 mb-6 border border-red-200">
                <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {error}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
                  <div className="rounded-lg bg-green-50 p-4 mb-6 border border-green-200">
                <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      {success}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            {/* Team Card */}
            {team && (
                                      <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-orange-300 transition-all duration-300">
                    <div className="text-center">
                      <div className="relative inline-block mb-4">
                <ProfileImage
                          className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white shadow-lg hover:ring-orange-200 transition-all duration-300"
                  alt={team.name}
                  image={team.logo}
                />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {team.name}
                </h3>
                      <p className="text-lg text-gray-500 mb-4">@{team.uniqueName}</p>
                {team.sport && (
                          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 mb-6">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                          </svg>
                    {team.sport}
                        </div>
                )}
                      
                  <button
                    onClick={handleSendRequest}
                    disabled={loading}
                          className="w-full inline-flex justify-center items-center px-6 py-4 border border-transparent text-base font-medium rounded-lg shadow-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
                          style={{
                            background: loading ? '#D1D5DB' : 'linear-gradient(to right, #E26735, #D45420)'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) {
                              e.target.style.background = 'linear-gradient(to right, #D45420, #C4501F)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!loading) {
                              e.target.style.background = 'linear-gradient(to right, #E26735, #D45420)';
                            }
                          }}
                        >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t("player.sendingRequest")}
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            {t("player.sendJoinRequest")}
                          </>
                        )}
                  </button>
                </div>
              </div>
            )}

                {/* Help Text */}
                {!team && !loading && (
                  <div className="text-center mt-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0124 24c4.004 0 7.625 2.371 9.287 6.286M32 14a6 6 0 11-12 0 6 6 0 0112 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t("player.findYourTeam")}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("player.findYourTeamDescription")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
    </div>
  );
};

export default TeamRequests;
